import Link from "@/components/LocaleLink";
import { useTranslation } from "react-i18next";
import BookDemoButton from "@/components/BookDemoButton";

type FooterLink = {
  labelKey: string;
  path: string;
  external?: boolean;
};

type FooterSection = {
  titleKey: string;
  links: FooterLink[];
};

const Footer = () => {
  const { t } = useTranslation();

  const footerLinks: Record<string, FooterSection> = {
    whoWeServe: {
      titleKey: "footer.whoWeServe",
      links: [
        { labelKey: "footer.smallBusiness", path: "/who-we-serve?size=small" },
        { labelKey: "footer.mediumBusiness", path: "/who-we-serve?size=medium" },
        { labelKey: "footer.enterprise", path: "/who-we-serve?size=enterprise" },
      ],
    },
    coreFeatures: {
      titleKey: "footer.coreFeatures",
      links: [
        { labelKey: "footer.photoVideoCapture", path: "/construction-photo-documentation" },
        { labelKey: "footer.trackJobProgress", path: "/construction-progress-tracking" },
        { labelKey: "footer.reports", path: "/construction-reports" },
        { labelKey: "features.aiEstimating.label", path: "/ai-construction-estimating" },
        { labelKey: "footer.projectFeed", path: "/construction-daily-log" },
        
        { labelKey: "features.documentIntel.label", path: "/construction-document-management" },
        { labelKey: "features.aiAssistant.label", path: "/ai-for-construction" },
      ],
    },
    advancedFeatures: {
      titleKey: "footer.advancedFeatures",
      links: [
        { labelKey: "features.riskManagement.label", path: "/construction-risk-management" },
        { labelKey: "footer.trackBuildersPerformance", path: "/contractor-invoicing" },
        { labelKey: "footer.galleriesTimelines", path: "/construction-scheduling" },
        { labelKey: "footer.shareUpdates", path: "/share-updates" },
        { labelKey: "footer.aiInspection", path: "/punch-list-software" },
        { labelKey: "features.compliance.label", path: "/construction-compliance" },
      ],
    },
    company: {
      titleKey: "footer.company",
      links: [
        { labelKey: "footer.pricing", path: "/pricing" },
        { labelKey: "footer.contact", path: "/contact" },
        { labelKey: "footer.integrations", path: "/integrations" },
        { labelKey: "footer.linkedin", path: "https://www.linkedin.com/company/banamind", external: true },
        { labelKey: "footer.g2", path: "https://www.g2.com/products/banamind/", external: true },
        { labelKey: "footer.capterra", path: "https://www.capterra.com/p/10042978/Banamind/", external: true },
        { labelKey: "footer.trustpilot", path: "https://www.trustpilot.com/review/banamind.ai", external: true },
      ],
    },
  };
  
  return (
    <footer className="bg-[#0a0a0a] text-white pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          {/* Logo and description */}
          <div className="lg:col-span-4 space-y-6">
            {/* Logo */}
            <span 
              className="text-3xl font-black tracking-tight text-white"
              style={{ fontFamily: "'Bebas Neue', 'Impact', 'Arial Narrow', sans-serif", letterSpacing: '0.05em' }}
            >
              BANAMIND
            </span>

            <p className="text-white/50 text-sm leading-relaxed max-w-sm">
              {t('footer.description')}
            </p>

            <div className="space-y-1 mb-6">
              <a href="mailto:support@banamind.com" className="block text-white/50 text-sm hover:text-white">
                support@banamind.com
              </a>
              <a href="mailto:press@banamind.com" className="block text-white/50 text-sm hover:text-white">
                press@banamind.com
              </a>
            </div>

            <BookDemoButton variant="withArrow" />
          </div>

          {/* Navigation columns */}
          <div className="lg:col-span-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {Object.values(footerLinks).map((section) => (
              <div key={section.titleKey}>
                <h4 className="font-bold text-white mb-4">{t(section.titleKey)}</h4>
                <ul className="space-y-3">
                  {section.links.map((link) => (
                    <li key={link.labelKey}>
                      {link.external ? (
                        <a
                          href={link.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white/50 text-sm hover:text-white transition-colors"
                        >
                          {t(link.labelKey)}
                        </a>
                      ) : (
                        <Link
                          to={link.path}
                          className="text-white/50 text-sm hover:text-white transition-colors"
                        >
                          {t(link.labelKey)}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col lg:flex-row justify-between items-center gap-6">
          {/* Legal links */}
          <div className="flex flex-wrap items-center gap-2 text-xs text-white/40">
            <span>©2026 Banamind LLC · License No. 2646864.01</span>
            <span>|</span>
            <Link to="/terms-of-service" className="hover:text-white">{t('footer.termsOfService')}</Link>
            <span>|</span>
            <Link to="/privacy-policy" className="hover:text-white">{t('footer.privacyPolicy')}</Link>
            <span>|</span>
            <Link to="/cookie-policy" className="hover:text-white">{t('footer.cookiePolicy')}</Link>
          </div>

        </div>
      </div>
    </footer>
  );
};

export default Footer;
